import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, DatePicker, Select, Space, Spin, Typography } from 'antd';
import dayjs from 'dayjs';
import {
  Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { getOpsResumo, getOpsBreakdown, getOpsAfiliadosPendentes } from '../../services/opsService';

const { Title } = Typography;
const { RangePicker } = DatePicker;

const PRESETS = [
  { value: 'mes_atual', label: 'Mês atual' },
  { value: 'mes_anterior', label: 'Mês anterior' },
  { value: '30d', label: 'Últimos 30 dias' },
  { value: '90d', label: 'Últimos 90 dias' },
  { value: 'custom', label: 'Personalizado' },
];

function rangeFromPreset(preset) {
  const hoje = dayjs();
  if (preset === 'mes_anterior') {
    const start = hoje.subtract(1, 'month').startOf('month');
    return [start, start.endOf('month')];
  }
  if (preset === '30d') {
    return [hoje.subtract(29, 'day').startOf('day'), hoje];
  }
  if (preset === '90d') {
    return [hoje.subtract(89, 'day').startOf('day'), hoje];
  }
  return [hoje.startOf('month'), hoje];
}

const formatCurrency = (v) =>
  Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatPct = (v) =>
  `${Number(v || 0).toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`;

function StatCard({ label, value }) {
  return (
    <div className="ops-stat-card">
      <div className="label">{label}</div>
      <div className="value">{value}</div>
    </div>
  );
}

function SectionTitle({ children }) {
  return <div className="ops-section-title">{children}</div>;
}

export default function OpsDashboardPage() {
  const [loading, setLoading] = useState(true);
  const [resumo, setResumo] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [afiliados, setAfiliados] = useState([]);
  const [preset, setPreset] = useState('mes_atual');
  const [range, setRange] = useState(() => rangeFromPreset('mes_atual'));
  const [planoTipo, setPlanoTipo] = useState('');
  const [billingType, setBillingType] = useState('');

  const filterParams = useMemo(() => {
    const params = {
      periodo_inicio: range[0].format('YYYY-MM-DD'),
      periodo_fim: range[1].format('YYYY-MM-DD'),
    };
    if (planoTipo) params.plano_tipo = planoTipo;
    if (billingType) params.billing_type = billingType;
    return params;
  }, [range, planoTipo, billingType]);

  const isMesAtual = useMemo(() => {
    const hoje = dayjs();
    return (
      range[0].isSame(hoje.startOf('month'), 'day')
      && range[1].isSame(hoje, 'day')
    );
  }, [range]);

  const periodoLabel = isMesAtual ? 'mês' : 'período';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, b, a] = await Promise.all([
        getOpsResumo(filterParams),
        getOpsBreakdown(filterParams),
        getOpsAfiliadosPendentes(),
      ]);
      setResumo(r);
      setHistorico(b.historico_mensal || []);
      setAfiliados(a.comissoes || []);
    } finally {
      setLoading(false);
    }
  }, [filterParams]);

  useEffect(() => {
    load();
  }, [load]);

  const onPresetChange = (value) => {
    setPreset(value);
    if (value !== 'custom') {
      setRange(rangeFromPreset(value));
    }
  };

  const onRangeChange = (dates) => {
    if (!dates || !dates[0] || !dates[1]) return;
    setPreset('custom');
    setRange(dates);
  };

  const churn = resumo?.churn_mes || {};
  const trialConv = resumo?.conversao_trial_mes || {};
  const estornos = resumo?.estornos_mes || {};
  const planos = resumo?.breakdown_plano || [];
  const formas = resumo?.breakdown_forma || [];
  const cancelamentos = resumo?.ultimos_cancelamentos || [];

  return (
    <div>
      <Title level={3}>Dashboard</Title>

      <div className="ops-filters">
        <Space wrap size="middle">
          <div className="ops-filter-item">
            <span className="ops-filter-label">Período</span>
            <Select
              value={preset}
              onChange={onPresetChange}
              options={PRESETS}
              style={{ width: 160 }}
            />
          </div>
          <div className="ops-filter-item">
            <span className="ops-filter-label">Intervalo</span>
            <RangePicker
              value={range}
              onChange={onRangeChange}
              format="DD/MM/YYYY"
              allowClear={false}
            />
          </div>
          <div className="ops-filter-item">
            <span className="ops-filter-label">Plano</span>
            <Select
              value={planoTipo}
              onChange={setPlanoTipo}
              style={{ width: 150 }}
              options={[
                { value: '', label: 'Todos' },
                { value: 'financeiro', label: 'Financeiro' },
                { value: 'milhas', label: 'Milhas' },
                { value: 'completo', label: 'Completo' },
              ]}
            />
          </div>
          <div className="ops-filter-item">
            <span className="ops-filter-label">Forma</span>
            <Select
              value={billingType}
              onChange={setBillingType}
              style={{ width: 140 }}
              options={[
                { value: '', label: 'Todas' },
                { value: 'PIX', label: 'PIX' },
                { value: 'CREDIT_CARD', label: 'Cartão' },
                { value: 'BOLETO', label: 'Boleto' },
              ]}
            />
          </div>
          <Button onClick={load} disabled={loading}>
            Atualizar
          </Button>
        </Space>
      </div>

      {loading && !resumo ? (
        <Spin size="large" />
      ) : (
        <>
          {loading && <div className="ops-loading-inline"><Spin size="small" /> Atualizando…</div>}

          <SectionTitle>Receita</SectionTitle>
          <div className="ops-card-grid">
            <StatCard label="MRR bruto" value={formatCurrency(resumo?.mrr || 0)} />
            <StatCard label="MRR líquido" value={formatCurrency(resumo?.mrr_liquido || 0)} />
            <StatCard label="ARR" value={formatCurrency(resumo?.arr || 0)} />
            <StatCard label="ARPU" value={formatCurrency(resumo?.arpu || 0)} />
            <StatCard label={`Receita ${periodoLabel} (bruta)`} value={formatCurrency(resumo?.receita_mes?.bruta || 0)} />
            <StatCard label={`Receita ${periodoLabel} (líquida)`} value={formatCurrency(resumo?.receita_mes?.liquida || 0)} />
          </div>

          <SectionTitle>Crescimento</SectionTitle>
          <div className="ops-card-grid">
            <StatCard label="Assinantes ativos" value={resumo?.assinantes_ativos ?? 0} />
            <StatCard label={`Novos no ${periodoLabel}`} value={resumo?.novas_assinaturas_mes ?? 0} />
            <StatCard label={`Cancelados no ${periodoLabel}`} value={resumo?.cancelamentos_mes ?? 0} />
            <StatCard label="Churn %" value={formatPct(churn.taxa_pct)} />
            <StatCard label="Crescimento líquido" value={resumo?.crescimento_liquido_mes ?? 0} />
          </div>

          <SectionTitle>Risco</SectionTitle>
          <div className="ops-card-grid">
            <StatCard label="Inadimplentes" value={resumo?.inadimplentes ?? 0} />
            <StatCard label="Em período de graça" value={resumo?.em_periodo_graca ?? 0} />
            <StatCard label={`Estornos no ${periodoLabel}`} value={estornos.quantidade ?? 0} />
            <StatCard label="Valor estornado" value={formatCurrency(estornos.valor_bruto || 0)} />
          </div>

          <SectionTitle>Trial</SectionTitle>
          <div className="ops-card-grid">
            <StatCard label="Trials ativos" value={resumo?.trial_ativos ?? 0} />
            <StatCard label={`Conversão do ${periodoLabel}`} value={formatPct(trialConv.taxa_pct)} />
            <StatCard
              label="Convertidos / encerrados"
              value={`${trialConv.convertidos ?? 0} / ${trialConv.trials_encerrados ?? 0}`}
            />
          </div>

          <div className="ops-panel">
            <Title level={5}>Receita — últimos 6 meses</Title>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={historico}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" />
                <YAxis tickFormatter={(v) => `R$${v}`} />
                <Tooltip formatter={(v) => formatCurrency(v)} />
                <Legend />
                <Bar dataKey="bruta" name="Bruta" fill="#1f4f4f" />
                <Bar dataKey="liquida" name="Líquida" fill="#b89040" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="ops-card-grid">
            <div className="ops-panel">
              <Title level={5}>Mix por plano</Title>
              {planos.length === 0 ? (
                <p>Nenhum assinante ativo.</p>
              ) : (
                <table className="ops-table">
                  <thead>
                    <tr>
                      <th>Plano</th>
                      <th>Qtd</th>
                      <th>MRR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {planos.map((p) => (
                      <tr key={`${p.tipo}-${p.ciclo}`}>
                        <td>{p.tipo} / {p.ciclo}</td>
                        <td>{p.quantidade}</td>
                        <td>{formatCurrency(p.mrr_parcial)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="ops-panel">
              <Title level={5}>Mix por forma de pagamento</Title>
              {formas.length === 0 ? (
                <p>Sem pagamentos no {periodoLabel}.</p>
              ) : (
                <table className="ops-table">
                  <thead>
                    <tr>
                      <th>Forma</th>
                      <th>Qtd</th>
                      <th>Bruto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formas.map((f) => (
                      <tr key={f.billing_type}>
                        <td>{f.label || f.billing_type}</td>
                        <td>{f.quantidade}</td>
                        <td>{formatCurrency(f.bruto)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div className="ops-card-grid">
            <div className="ops-panel">
              <Title level={5}>Últimos cancelamentos</Title>
              {cancelamentos.length === 0 ? (
                <p>Nenhum cancelamento recente.</p>
              ) : (
                <ul className="ops-list">
                  {cancelamentos.map((c) => (
                    <li key={`${c.email}-${c.data_fim}`}>
                      {c.email} — {c.plano} ({c.data_fim})
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="ops-panel">
              <Title level={5}>Faturas</Title>
              <p>Pagas: {resumo?.faturas?.pagas ?? 0}</p>
              <p>Pendentes: {resumo?.faturas?.pendentes ?? 0}</p>
              <p>Vencidas: {resumo?.faturas?.vencidas ?? 0}</p>
              <p>Próximas: {resumo?.faturas?.proximas ?? 0}</p>
            </div>
            <div className="ops-panel">
              <Title level={5}>Comissões afiliados a pagar</Title>
              {afiliados.length === 0 ? (
                <p>Nenhuma comissão confirmada pendente.</p>
              ) : (
                <ul className="ops-list">
                  {afiliados.slice(0, 8).map((c) => (
                    <li key={c.id}>
                      {c.afiliado_email} — {formatCurrency(c.valor_comissao)} ({c.indicado_email})
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
